import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, exportApi, importApi } from '../api/client';
import AnnouncementsCard from '../components/dashboard/AnnouncementsCard';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardBody } from '../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/Table';
import { formatDate, getToday } from '../lib/dateUtils';
import { formatNumber, downloadBlob } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  FileText,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSpreadsheet,
  FileDown,
} from 'lucide-react';

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  // Fetch dashboard data
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => dashboardApi.getSummary(),
    select: (res) => res.data,
  });

  const { data: divisionBreakdown } = useQuery({
    queryKey: ['dashboard', 'division-breakdown'],
    queryFn: () => dashboardApi.getDivisionBreakdown(),
    select: (res) => res.data,
  });

  const { data: weeklyTrend } = useQuery({
    queryKey: ['dashboard', 'weekly-trend'],
    queryFn: () => dashboardApi.getWeeklyTrend(),
    select: (res) => res.data,
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  // Blank 施工彙整表 template to fill in and import
  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const response = await importApi.downloadTemplate();
      downloadBlob(response.data, '施工彙整表_空白範本.xlsx');
    } catch (error) {
      console.error('Template download error:', error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  // Download today's 施工彙整表 Excel
  const handleDownloadToday = async () => {
    const today = getToday();
    setIsDownloading(true);
    try {
      const response = await exportApi.exportReports({ date: today }, 'excel');
      downloadBlob(response.data, `TPC_Report_${today}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tpc-blue"></div>
      </div>
    );
  }

  const summaryCards = [
    {
      label: t('dashboard.totalProjects'),
      value: summary?.totalProjects || 0,
      icon: FileText,
      color: 'text-tpc-blue',
      bgColor: 'bg-blue-50',
    },
    {
      label: t('dashboard.totalWorkers'),
      value: formatNumber(summary?.totalWorkers || 0, locale),
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: t('dashboard.normal'),
      value: summary?.normal || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: t('dashboard.overtime'),
      value: summary?.overtime || 0,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: t('dashboard.holiday'),
      value: summary?.holiday || 0,
      icon: AlertCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      label: t('dashboard.holidayOvertime'),
      value: summary?.holiday_overtime || 0,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('dashboard.title')}
          </h1>
          <p className="text-gray-500 mt-1">
            {formatDate(getToday(), locale)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate} isLoading={isDownloadingTemplate}>
            <FileDown className="w-4 h-4 mr-2" />
            {t('dashboard.downloadTemplate', '下載施工彙整表範本Excel')}
          </Button>
          <Button variant="outline" onClick={handleDownloadToday} isLoading={isDownloading}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {t('dashboard.downloadTodayExcel', '下載本日施工彙整表 Excel')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {summaryCards.map((card, index) => (
          <Card key={index}>
            <CardBody className="p-4">
              <div className={`inline-flex p-2 rounded-lg ${card.bgColor} mb-3`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <p className="text-sm text-gray-500">{card.label}</p>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Division Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.divisionBreakdown')}</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('reports.division')}</TableHead>
                  <TableHead className="text-right">{t('dashboard.totalProjects')}</TableHead>
                  <TableHead className="text-right">{t('dashboard.totalWorkers')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {divisionBreakdown?.length > 0 ? (
                  divisionBreakdown.map((row) => (
                    <TableRow key={row.division}>
                      <TableCell className="font-medium">{row.division}</TableCell>
                      <TableCell className="text-right">{row.total}</TableCell>
                      <TableCell className="text-right">{formatNumber(row.totalWorkers, locale)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                      {t('dashboard.noReportsToday')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        {/* Weekly Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.weeklyTrend')}</CardTitle>
          </CardHeader>
          <CardBody>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(date) => formatDate(date, locale)}
                    formatter={(value, name) => [
                      value,
                      name === 'count' ? t('dashboard.totalProjects') : t('dashboard.totalWorkers'),
                    ]}
                  />
                  <Bar dataKey="count" fill="#1E3A8A" name="count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* 最新動態: 站務公告 / 注意事項 */}
      <AnnouncementsCard />

    </div>
  );
}
